/**
 * Chip interativo para dados extraídos com navegação contextual
 */

import React, { useState } from 'react'
import {
  Badge,
  Box,
  Chip,
  IconButton,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import WarningIcon from '@mui/icons-material/Warning'
import { ExtractedDataWithContext } from '../../../services/transcriptionAnalysis'

type Props = {
  data: ExtractedDataWithContext
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default'
  label?: string
  onNavigate?: (segmentIndex: number) => void
  speakerMapping?: Record<string, string>
  isMobile?: boolean
}

function formatTime(time: string | number): string {
  if (time == null) return '0:00'
  
  const timeStr = String(time)
  if (!timeStr) return '0:00'
  
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':')
    if (parts.length === 3) {
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    return timeStr
  }
  
  const seconds = parseFloat(timeStr)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function ExtractedDataChip({
  data,
  color = 'default',
  label,
  onNavigate,
  speakerMapping = {},
  isMobile = false
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const hasMultipleOccurrences = data.occurrences.length > 1

  // Detecta possíveis contradições (valores diferentes para mesma categoria)
  const uniqueValues = new Set(data.occurrences.map(occ => occ.value))
  const hasConflict = uniqueValues.size > 1

  const displayLabel = label || data.value
  const getSpeakerName = (speaker: string) => speakerMapping[speaker] || speaker

  return (
    <>
      <Badge
        badgeContent={hasMultipleOccurrences ? `${data.occurrences.length}x` : undefined}
        color={hasConflict ? 'error' : 'default'}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: isMobile ? '0.55rem' : '0.65rem',
            height: isMobile ? 14 : 16,
            minWidth: isMobile ? 14 : 16
          }
        }}
      >
        <Chip
          label={displayLabel}
          size="small"
          color={hasConflict ? 'error' : color}
          variant="outlined"
          onClick={handleClick}
          onDelete={hasMultipleOccurrences ? handleClick : undefined}
          deleteIcon={hasConflict ? <WarningIcon /> : <InfoIcon />}
          sx={{
            fontSize: isMobile ? '0.65rem' : '0.8125rem',
            height: isMobile ? 22 : 24,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 1,
              transform: 'translateY(-1px)',
              transition: 'all 0.2s'
            }
          }}
        />
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: isMobile ? 280 : 400,
              maxHeight: isMobile ? 300 : 400,
              mt: 1
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {data.value}
            </Typography>
            <Chip
              label={`${data.occurrences.length} menção${data.occurrences.length > 1 ? 'ões' : ''}`}
              size="small"
              color="primary"
              sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', height: isMobile ? 20 : 22 }}
            />
          </Box>

          {hasConflict && (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}>
              ⚠️ Valores diferentes detectados
            </Alert>
          )}

          <Divider sx={{ mb: 1 }} />

          <List dense sx={{ maxHeight: isMobile ? 200 : 280, overflow: 'auto' }}>
            {data.occurrences.map((occ, index) => (
              <ListItem
                key={index}
                sx={{
                  px: 0,
                  py: 1,
                  borderBottom: index < data.occurrences.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: '#666',
                            fontSize: isMobile ? '0.65rem' : '0.75rem'
                          }}
                        >
                          {formatTime(occ.timestamp)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                          •
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#666',
                            fontSize: isMobile ? '0.65rem' : '0.75rem'
                          }}
                        >
                          #{occ.segmentIndex + 1}
                        </Typography>
                      </Box>
                      {onNavigate && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            onNavigate(occ.segmentIndex)
                            handleClose()
                          }}
                          sx={{
                            p: 0.25,
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText'
                            }
                          }}
                        >
                          <ArrowForwardIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 500,
                          fontSize: isMobile ? '0.65rem' : '0.75rem',
                          display: 'block',
                          mb: 0.25
                        }}
                      >
                        {getSpeakerName(occ.speaker)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#555',
                          fontSize: isMobile ? '0.7rem' : '0.8125rem',
                          fontStyle: 'italic',
                          lineHeight: 1.4
                        }}
                      >
                        "{occ.context}"
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </ListItem>
            ))}
          </List>

          {onNavigate && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
              <Typography variant="caption" sx={{ color: '#999', fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                💡 Clique em <ArrowForwardIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> para navegar até o segmento
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  )
}
